import { FC, KeyboardEvent, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useAlert } from "capitalrx-components-2.0/lib/general";
import { Form } from "capitalrx-components-2.0/lib/forms";
import { Grid, Shelf, Stack } from "capitalrx-components-2.0/lib/layout";

import { ThemedSubmitButton } from "../themed";
import "./VerifyCodeForm.scss";

export interface VerifyCodeFormData {
  readonly code01: string;
  readonly code02: string;
  readonly code03: string;
  readonly code04: string;
  readonly code05: string;
  readonly code06: string;
}

export interface VerifyCodeFormProps {
  readonly handleVerifyCode: (fullCode: string) => Promise<void>;
}

const validateCode = (code: string): boolean => {
  return code !== null && code !== undefined && code.trim() !== "" && !isNaN(parseInt(code));
};

export const VerifyCodeForm: FC<VerifyCodeFormProps> = ({ handleVerifyCode }) => {
  const { t: translate } = useTranslation();
  const form = useForm<VerifyCodeFormData>();
  const alert = useAlert();

  const code01 = form.watch("code01");
  const code02 = form.watch("code02");
  const code03 = form.watch("code03");
  const code04 = form.watch("code04");
  const code05 = form.watch("code05");
  const code06 = form.watch("code06");

  const allCodesValid
    = validateCode(code01)
    && validateCode(code02)
    && validateCode(code03)
    && validateCode(code04)
    && validateCode(code05)
    && validateCode(code06);

  // this one sets the first text box focused on initial render
  useEffect(() => {
    form.setFocus("code01");
  }, []);

  // this one auto submits the form when the codes are all filled out
  useEffect(() => {
    if (allCodesValid) {
      form.handleSubmit(onSubmit)();
    }
  }, [code01, code02, code03, code04, code05, code06]);

  /**
   * These ones focus the next code box when a valid code is entered in,
   * and clear the code box if non-number values are entered
   */
  useEffect(() => {
    if (validateCode(code01)) {
      form.setFocus("code02");
    } else {
      form.setValue("code01", "", {
        shouldValidate: false,
        shouldDirty: false,
        shouldTouch: false,
      });
    }
  }, [code01]);

  useEffect(() => {
    if (validateCode(code02)) {
      form.setFocus("code03");
    } else {
      form.setValue("code02", "", {
        shouldValidate: false,
        shouldDirty: false,
        shouldTouch: false,
      });
    }
  }, [code02]);

  useEffect(() => {
    if (validateCode(code03)) {
      form.setFocus("code04");
    } else {
      form.setValue("code03", "", {
        shouldValidate: false,
        shouldDirty: false,
        shouldTouch: false,
      });
    }
  }, [code03]);

  useEffect(() => {
    if (validateCode(code04)) {
      form.setFocus("code05");
    } else {
      form.setValue("code04", "", {
        shouldValidate: false,
        shouldDirty: false,
        shouldTouch: false,
      });
    }
  }, [code04]);

  useEffect(() => {
    if (validateCode(code05)) {
      form.setFocus("code06");
    } else {
      form.setValue("code05", "", {
        shouldValidate: false,
        shouldDirty: false,
        shouldTouch: false,
      });
    }
  }, [code05]);

  useEffect(() => {
    if (!validateCode(code06)) {
      form.setValue("code06", "", {
        shouldValidate: false,
        shouldDirty: false,
        shouldTouch: false,
      });
    }
  }, [code06]);

  const onKeyDown = (inputName: keyof VerifyCodeFormData) => {
    return (keyEvent: KeyboardEvent) => {
      if (keyEvent.key === "Backspace") {
        form.setValue(inputName, "");
        /**
         * There's some weird render timing happening here where when you
         * backspace out codeN, the form watch for codeN-1 is reporting an empty
         * string, which causes both codeN and codeN-1 to be cleared out.
         *
         * This setTimeout seems to remedy that issue, as it gives the form
         * watch a render cycle to catch up to the true form state.
         */
        setTimeout(() => {
          if (inputName !== "code01") {
            const codeAsNum = +inputName.replace("code0", "");
            const previousInput = `code0${codeAsNum - 1}` as keyof VerifyCodeFormData;
            form.setFocus(previousInput, { shouldSelect: true });
          }
        });
      }
    };
  };

  const onSubmit = async (formData: VerifyCodeFormData) => {
    const fullCode = `${formData.code01}${formData.code02}${formData.code03}${formData.code04}${formData.code05}${formData.code06}`;
    try {
      await handleVerifyCode(fullCode);
    } catch (error) {
      alert({
        message: error,
        type: "error",
        style: "banner",
      });
      form.setFocus("code01", { shouldSelect: true });
      form.reset();
    }
  };

  return (
    <Form form={form} onSubmit={onSubmit}>
      <Stack>
        <Shelf spacing="mini">
          <input
            type="text"
            onKeyDown={(event) => onKeyDown("code01")(event)}
            className="codeTextbox"
            maxLength={1}
            {...form.register("code01")}
          />
          <input
            type="text"
            onKeyDown={(event) => onKeyDown("code02")(event)}
            className="codeTextbox"
            maxLength={1}
            {...form.register("code02")}
          />
          <input
            type="text"
            onKeyDown={(event) => onKeyDown("code03")(event)}
            className="codeTextbox"
            maxLength={1}
            {...form.register("code03")}
          />
          <input
            type="text"
            onKeyDown={(event) => onKeyDown("code04")(event)}
            className="codeTextbox"
            maxLength={1}
            {...form.register("code04")}
          />
          <input
            type="text"
            onKeyDown={(event) => onKeyDown("code05")(event)}
            className="codeTextbox"
            maxLength={1}
            {...form.register("code05")}
          />
          <input
            type="text"
            onKeyDown={(event) => onKeyDown("code06")(event)}
            className="codeTextbox"
            maxLength={1}
            {...form.register("code06")}
          />
        </Shelf>
        <Grid>
          <ThemedSubmitButton disabled={!allCodesValid}>{translate("MULTIFACTOR_AUTHENTICATION.verifyCodeForm.verifyCode")}</ThemedSubmitButton>
        </Grid>
      </Stack>
    </Form>
  );
};
